import { useCallback, useMemo } from "react";
import { Stack } from "@mui/material";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import type {
  IncidentFormId,
  IncidentWorkItemWithContext,
  RecordPlaceOfSafetyMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { Banner } from "../../components/Banner/Banner.js";
import type { LeavePlaceOfSafetyFormProps } from "../../forms/LeavePlaceOfSafety/LeavePlaceOfSafetyForm.js";
import {
  LeavePlaceOfSafetyForm,
  LeavePlaceOfSafetyFormSkeleton,
} from "../../forms/LeavePlaceOfSafety/LeavePlaceOfSafetyForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:LeavePlaceOfSafetyPage");

export type LeavePlaceOfSafetyPageProps = Record<string, unknown>;

export function LeavePlaceOfSafetyPage(_props: LeavePlaceOfSafetyPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const placeOfSafetyFormId = IncidentFormIdSchema.safeParse(params.formId);
  if (!placeOfSafetyFormId.success) {
    throw new Error("Invalid form ID");
  }

  if (incidentWorkItemError) {
    return <ErrorComponent />;
  }

  return (
    <LeavePlaceOfSafetyPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
      formId={placeOfSafetyFormId.data}
    />
  );
}

export type LeavePlaceOfSafetyPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
  formId: IncidentFormId;
};

export function LeavePlaceOfSafetyPageInner({
  incidentWorkItem,
  refetchWorkItem,
  formId,
}: LeavePlaceOfSafetyPageInnerProps) {
  return incidentWorkItem === undefined ? (
    <LeavePlaceOfSafetyPageInnerLoading />
  ) : (
    <LeavePlaceOfSafetyPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
      formId={formId}
    />
  );
}

function LeavePlaceOfSafetyPageInnerLoading() {
  return <LeavePlaceOfSafetyFormSkeleton />;
}

export function LeavePlaceOfSafetyPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
  formId,
}: Required<LeavePlaceOfSafetyPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  // Get incident information
  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  // Get place of safety information
  const placeOfSafetyName = useMemo(
    () =>
      (
        incidentWorkItem.forms
          .find(
            (form) =>
              form.formType ===
                IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
              form.id === formId,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
          )?.data as RecordPlaceOfSafetyMinimumFields | undefined
      )?.placeOfSafety.name,
    [formId, incidentWorkItem.forms],
  );

  if (!placeOfSafetyName) {
    throw new Error("Place of safety name not collected");
  }

  // Get the schema for the "leave place-of-safety" form
  const schema = useMemo(
    () =>
      workflows[workflowVersion]?.recordPlaceOfSafety.formDefinition.sections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
        )
        ?.buildFormSpecification({ incidentWorkItem }).formSchema,
    [workflowVersion, incidentWorkItem],
  );

  if (!schema) {
    throw new Error("Schema not found");
  }

  // Calculate the initial values for the form
  const [initialValues, initialValuesWereValid] = useMemo(() => {
    // Get the existing values from the form if it exists
    const existingValues =
      incidentWorkItem.forms
        .find(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety &&
            form.id === formId,
        )
        ?.sections.find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
        )?.data ?? {};

    const existingValuesWereValid = schema.safeParse(existingValues).success;

    // This is where we will acculmulate the values for the form
    const finalValues: Record<string, unknown> = { ...existingValues };

    return [finalValues, existingValuesWereValid];
  }, [formId, incidentWorkItem.forms, schema]);

  // Should we enable auto-save?
  const enableAutoSave = useMemo(
    () =>
      workflows[
        workflowVersion
      ]?.recordPlaceOfSafety.formDefinition.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    LeavePlaceOfSafetyFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (initialValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }

      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          formId,
          FormSectionSchema.enum.recordPlaceOfSafetyLeave,
          values,
          initialValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [formId, incidentWorkItemId, initialValuesWereValid, schema],
  );

  const onSubmit: Exclude<
    LeavePlaceOfSafetyFormProps<typeof workflowVersion>["onSubmit"],
    undefined
  > = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        // Populate it with values
        const populateFormResponse = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          formId,
          FormSectionSchema.enum.recordPlaceOfSafetyLeave,
          values,
          true,
        );
        if (populateFormResponse.status !== 204) {
          throw Error(JSON.stringify(populateFormResponse.data.message));
        }

        // Refetch the data as we have mutated the incident work item.
        await refetchWorkItem();

        navigate(
          createRoute.incidentReviewPlacesOfSafetyPage(incidentWorkItemId),
        );
        return;
      } catch (error) {
        debug("Failed to update Leave section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      formId,
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      t,
      toastNotifications,
    ],
  );

  const onCancel: Exclude<
    LeavePlaceOfSafetyFormProps<typeof workflowVersion>["onCancel"],
    undefined
  > = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <Stack
      sx={{
        margin: "1rem",
      }}
    >
      <Banner
        bannerType={"info"}
        body={{
          translationKey: tk("leavePlaceOfSafety.banner"),
          translationOptions: { placeOfSafetyName },
        }}
      />
      <LeavePlaceOfSafetyForm
        initialValues={initialValues}
        incidentWorkItem={incidentWorkItem}
        onAutoSave={enableAutoSave ? onAutoSave : undefined}
        onSubmit={onSubmit}
        onCancel={onCancel}
        workflowVersion={workflowVersion}
      />
    </Stack>
  );
}
